import React, { useEffect, useRef, useState } from 'react';
import { LoaderCircle, Navigation } from 'lucide-react';
import { getCurrentLocationErrorMessage } from '../utils/mapCurrentLocation';
import { haptic } from '../utils/vibration';

const MapCurrentLocationButton = ({
  onLocated,
  onError,
  className = '',
  disabled = false,
}) => {
  const mountedRef = useRef(true);
  const [isLocating, setIsLocating] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClick = async () => {
    if (disabled || isLocating) return;

    haptic('tap');

    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      onError?.(getCurrentLocationErrorMessage({ kind: 'insecure-context' }));
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      onError?.(getCurrentLocationErrorMessage({ kind: 'unsupported' }));
      return;
    }

    const requestPosition = (options) => new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });

    setIsLocating(true);
    try {
      let position;
      try {
        position = await requestPosition({
          enableHighAccuracy: false,
          timeout: 20000,
          maximumAge: 300000,
        });
      } catch (firstError) {
        const errorCode = Number(firstError?.code);
        if (errorCode !== 2 && errorCode !== 3) {
          throw firstError;
        }

        position = await requestPosition({
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 600000,
        });
      }

      if (!mountedRef.current) return;
      const longitude = Number(position?.coords?.longitude);
      const latitude = Number(position?.coords?.latitude);
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        onError?.(getCurrentLocationErrorMessage());
        return;
      }
      haptic('selection');
      onLocated?.([longitude, latitude], position);
    } catch (error) {
      if (!mountedRef.current) return;
      onError?.(getCurrentLocationErrorMessage(error));
    } finally {
      if (mountedRef.current) {
        setIsLocating(false);
      }
    }
  };

  const label = isLocating ? 'Определяем местоположение…' : 'Моё местоположение';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isLocating}
      className={`pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white/95 text-blue-600 shadow-sm backdrop-blur transition-colors hover:bg-white disabled:cursor-wait disabled:opacity-70 dark:border-gray-700 dark:bg-gray-900/95 dark:text-blue-400 dark:hover:bg-gray-900 ${className}`}
      title={label}
      aria-label={label}
      aria-busy={isLocating}
    >
      {isLocating ? (
        <LoaderCircle className="h-4 w-4 animate-spin" />
      ) : (
        <Navigation className="h-4 w-4 fill-current" />
      )}
      <span className="sr-only">{label}</span>
    </button>
  );
};

export default MapCurrentLocationButton;
